"use client";

import { useEffect, useRef } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import {
  getAnalyticsSurfaceFromPath,
  getTelemetrySubtabFromLocation,
  getTopLevelTabFromPath,
} from "@/lib/analytics/routing";
import type { TelemetryEventPayload } from "@/lib/analytics/events";
import {
  getTelemetryPlatform,
  getTelemetrySessionId,
  postTelemetryEvent,
} from "@/lib/analytics/client";

export function RouteTelemetryTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const searchKey = searchParams?.toString() ?? "";
  const previousTabRef = useRef<string | null>(null);
  const previousSubtabRef = useRef<string | null>(null);

  useEffect(() => {
    if (!pathname) return;

    const surface = getAnalyticsSurfaceFromPath(pathname);
    if (surface === "public") return;

    const sessionId = getTelemetrySessionId();
    const platform = getTelemetryPlatform();

    void postTelemetryEvent<"screen_view">({
      eventName: "screen_view",
      payload: {
        surface,
        path: pathname,
      } satisfies TelemetryEventPayload<"screen_view">,
      appSurface: surface,
      sessionId,
      platform,
    });

    const tab = getTopLevelTabFromPath(pathname);
    if (!tab) return;

    const tabKey = `${surface}:${tab}`;
    if (previousTabRef.current === tabKey) return;
    previousTabRef.current = tabKey;

    void postTelemetryEvent<"top_level_tab_opened">({
      eventName: "top_level_tab_opened",
      payload: {
        surface,
        tab,
      } satisfies TelemetryEventPayload<"top_level_tab_opened">,
      appSurface: surface,
      sessionId,
      platform,
    });
  }, [pathname]);

  useEffect(() => {
    if (!pathname) return;

    const surface = getAnalyticsSurfaceFromPath(pathname);
    if (surface === "public") return;

    const subtab = getTelemetrySubtabFromLocation(pathname, searchKey);
    if (!subtab) return;

    const subtabKey = `${surface}:${subtab.tab}:${subtab.subtab}`;
    if (previousSubtabRef.current === subtabKey) return;
    previousSubtabRef.current = subtabKey;

    void postTelemetryEvent<"subtab_opened">({
      eventName: "subtab_opened",
      payload: {
        surface,
        tab: subtab.tab,
        subtab: subtab.subtab,
      } satisfies TelemetryEventPayload<"subtab_opened">,
      appSurface: surface,
      sessionId: getTelemetrySessionId(),
      platform: getTelemetryPlatform(),
    });
  }, [pathname, searchKey]);

  return null;
}
